<template>
  <div class="basket-price-list-desktop">
    <h1 class="basket-price-list-desktop__title" data-test-price-list-title v-html="headline" />
    <div class="basket-price-list-desktop__content flex-container flex-dir-column">
      <div
        v-if="areProposalsNonEmpty"
        class="basket-price-list-desktop__items"
        data-test-basket-price-list-items
      >
        <basket-price-list-item
          v-for="(proposal, index) in proposals"
          :key="index"
          data-test-in-basket-price-list-item
          :proposal="proposal"
          :per-year-label="perYearLabel"
          :digital-bonus-label="digitalBonusLabel"
          :digital-bonus-tooltip="digitalBonusTooltip"
          :year-zero-text="yearZeroText"
          :year-zero-modal="yearZeroModal"
        />
      </div>
      <div class="basket-price-list-desktop-total flex-container flex-dir-column">
        <div class="flex-container align-middle align-justify">
          <h3
            v-if="totalPriceLabel"
            class="basket-price-list-desktop-total__title"
            data-test-price-list-total-label
            v-html="totalPriceLabel"
          />
          <p
            v-if="showTotalPrice"
            class="basket-price-list-desktop-total__price"
            data-test-price-list-total
          >
            <span class="basket-price-list-desktop-total__price-number" v-html="totalPrice" />
            <span v-html="perYearLabel" />
          </p>
        </div>
        <div
          v-if="showTotalFees"
          class="basket-price-list-desktop-total__fees flex-container align-justify"
          data-test-price-list-fees
        >
          <p v-html="totalFeesLabel" />
          <p v-html="formatPrice(totalFees)" />
        </div>
      </div>
      <div v-if="$slots.actions" class="basket-price-list-desktop__actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import BasketPriceListItem from './BasketPriceListItem.vue';
import { ProposalPriceModel } from './types/ProposalPriceList';
import { ChildYearZeroModal } from '@/components/Feature/ProposalBasket/types/ProposalBasket';
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'BasketPriceListDesktop',
  components: {
    BasketPriceListItem
  },
  props: {
    proposals: {
      type: Array as PropType<Array<ProposalPriceModel>>,
      default: () => [] as ProposalPriceModel[]
    },
    headline: {
      type: String,
      default: () => ''
    },
    totalPriceLabel: {
      type: String,
      default: () => ''
    },
    totalFeesLabel: {
      type: String,
      default: () => ''
    },
    perYearLabel: {
      type: String,
      default: () => ''
    },
    totalPrice: {
      type: String,
      default: () => ''
    },
    totalFees: {
      type: String,
      default: () => ''
    },
    isStickyOnMobile: {
      type: Boolean,
      default: () => true
    },
    digitalBonusLabel: {
      type: String,
      default: () => ''
    },
    digitalBonusTooltip: {
      type: String,
      default: () => ''
    },
    yearZeroText: {
      type: String,
      default: () => ''
    },
    yearZeroModal: {
      type: Object as PropType<ChildYearZeroModal>,
      default() {
        return {
          textLink: '',
          title: '',
          textContent: '',
          closeBtn: ''
        };
      }
    }
  },
  data() {
    return {
      mobileListOpen: false
    };
  },
  computed: {
    showTotalPrice(): boolean {
      return this.totalPrice !== undefined && this.totalPrice !== '';
    },
    showTotalFees(): boolean {
      return this.totalFees !== undefined && this.totalFees !== '';
    },
    areProposalsNonEmpty(): boolean {
      return this.proposals && this.proposals.length > 0;
    }
  },
  methods: {
    formatPrice(price: string): string {
      return `${price} ${this.perYearLabel}`;
    }
  }
});
</script>
<style lang="scss" scoped>
@import 'src/styles/all.scss';

.basket-price-list-desktop {
  @include breakpoint(medium down) {
    display: none;
  }

  height: fit-content;
  width: 100%;
  background: $body-background;
  padding: rem-calc(8);
  border-radius: rem-calc(15) rem-calc(15) 0 0;

  &__title {
    text-align: center;
    margin: 0;
  }

  &__content {
    background: white;
    border: 1px solid $medium-gray;
  }

  &__actions {
    border-top: 1px solid $medium-gray;
    padding: rem-calc(20) rem-calc(30) 0;
  }
}

.basket-price-list-desktop-total {
  padding: rem-calc(20) rem-calc(30);

  &__title {
    margin-bottom: 0;
  }

  &__price {
    font-weight: 700;
    font-size: rem-calc(14);
    margin-bottom: rem-calc(4);

    &-number {
      font-size: rem-calc(25);
      margin-right: rem-calc(4);
    }
  }

  &__fees p {
    margin-bottom: rem-calc(4);
  }
}
</style>
